import clsx from 'clsx';
import { Icon } from '@/components/icons/svg-icons';
import { Button } from '@nf/ui';
import { bettingPreferenceModalStyle } from './betting-preference-modal.style';
import { OddsTableImg, BetslipBasicImg, BetslipFloatImg } from './betting-preference-img';
import { ModalHeader, ScrollableModal } from '@/components/widgets/scrollable-modal';
import { BetslipStyle } from '@/hooks/query';
import { BettingMode, bettingModeSettings } from '@/features/betslip/betting-mode';
import { useTranslations } from 'next-intl';
import { bettingPreferenceService } from '@/services/betting-preference.service';
import { profilePanelService } from '@/services/profile-panel.service';
import { useObservableState } from '@nf/utils-common/hooks';
import { useBettingPrefer } from '@/hooks';
import { KafkaActionSettings, KafkaMarsTypeEnums, KafkaNote } from '@nf/utils-common/constants';
import { ViewEnum } from '@/types';
import { setKafkaNormal } from '@/hooks/query/use-kafka-log';
import { useAppearanceStore } from '@/store/appearance';


const betslipLayout = [
	{ title: 'lbl_Basic', value: BetslipStyle.basic },
	{ title: 'lbl_Floating', value: BetslipStyle.floating }
];

export const BettingPreferenceModal = () => {
	const t = useTranslations('General');
	const isShowBettingPreferenceModal = useObservableState(bettingPreferenceService.bettingPreferenceModal$, false);
	const isFirstTime = useObservableState(bettingPreferenceService.isFirstTime$, false);
	const view = useAppearanceStore(state => state.view);
	const { selectedBettingMode, selectBettingMode, selectedBetslipStyle, selectBetslipStyle,  saveBettingPrefer } = useBettingPrefer({
		initialMode: BettingMode.Independent,
		initialStyle: BetslipStyle.basic,
		syncOnCondition: isShowBettingPreferenceModal,
	});

	const handleSelectBetslipStyle = (betslipStyle: number) => {
		const kafkaData = view === ViewEnum.galaxy ? {
			note: KafkaNote.GalaxyV2,
			type: 11,
			action: 21
		} : {
			note: KafkaNote.MarsV1,
			type: 7,
			action: 34
		};

		setKafkaNormal(kafkaData);
		selectBetslipStyle(betslipStyle);
	};

	const handleSelectBettingMode = (betslipStyle: number) => {
		const kafkaData = view === ViewEnum.galaxy ? {
			note: KafkaNote.GalaxyV2,
			type: 11,
			action: 20
		} : {
			note: KafkaNote.MarsV1,
			type: 7,
			action: 33
		};

		setKafkaNormal(kafkaData);
		selectBettingMode(betslipStyle);
	};

	const handleSaveBettingMode = () => {
		const action = selectedBettingMode === BettingMode.Independent ? 'IndependentBetting' : 'MultipleBetting';
		setKafkaNormal({
			note: KafkaNote.MarsV1,
			type: KafkaMarsTypeEnums.QuickSettingAndMe,
			action: KafkaActionSettings.MarsV1[KafkaMarsTypeEnums.QuickSettingAndMe][action]
		});
	};

	const closeModal = () => {
		bettingPreferenceService.displayBettingPreferenceModal(false);

		if (isFirstTime) {
			profilePanelService.displayProfilePanel(true, true);
		}
	};

	const saveBettingPreference = () => {
		handleSaveBettingMode();
		saveBettingPrefer();
		closeModal();
	};

	return (
		<>
			<ScrollableModal
				sectionName={bettingPreferenceModalStyle.sectionName}
				className={clsx(bettingPreferenceModalStyle.container, 'modal')}
				isOpen={isShowBettingPreferenceModal}
				closeModal={closeModal}
				header={(
					<ModalHeader
						className={bettingPreferenceModalStyle.header}
						title={t('lbl_BettingPreferences')}
						closeModal={closeModal}
					/>
				)}
			>
				<div className="sports-priority">
					<div className={clsx('scrollbar', bettingPreferenceModalStyle.scrollbar)}>
						<div className={bettingPreferenceModalStyle.panel}>
							<div className={bettingPreferenceModalStyle.title}>{t('lbl_BettingMode')}</div>
							<div className={bettingPreferenceModalStyle.content}>
								{bettingModeSettings.map((mode) => (
									<div
										key={mode.mode}
										className={bettingPreferenceModalStyle.rowItem}
										onClick={() => handleSelectBettingMode(mode.mode)}
									>
										<div className={bettingPreferenceModalStyle.rowRadio}>
											<Icon name={mode.mode === selectedBettingMode ? 'radio-on' : 'radio'} />
										</div>
										<div className={bettingPreferenceModalStyle.rowAside}>
											<div
												data-active={mode.mode === selectedBettingMode}
												className={bettingPreferenceModalStyle.rowTitle}
											>
												{t(mode.label)}
												{mode.mode === selectedBettingMode && (
													<div className={bettingPreferenceModalStyle.rowControl}>
														<Icon name="ticket-success-line" />
													</div>
												)}
											</div>
											<div className={bettingPreferenceModalStyle.rowContent}>
												{t(mode.descriptionLabel)}
											</div>
										</div>
									</div>
								))}
							</div>
						</div>

						<div className={bettingPreferenceModalStyle.panel}>
							<div className={bettingPreferenceModalStyle.title}>{t('lbl_BetSlipStyle')}</div>
							<div className={clsx(bettingPreferenceModalStyle.content, bettingPreferenceModalStyle.contentHorizontal)}>
								{betslipLayout.map(item =>
									<div
										key={item.value}
										className={clsx('group', bettingPreferenceModalStyle.columnItem)}
										data-active={selectedBetslipStyle === item.value}
										onClick={() => handleSelectBetslipStyle(item.value)}
									>
										<div>{t(item.title)}</div>
										<div className={bettingPreferenceModalStyle.img}>
											<OddsTableImg />
											<div className={bettingPreferenceModalStyle.betslipImg}>
												{item.value === BetslipStyle.basic ?
													<BetslipBasicImg active={selectedBetslipStyle === item.value} /> :
													<BetslipFloatImg active={selectedBetslipStyle === item.value} />}
											</div>
										</div>
										<Icon name={selectedBetslipStyle === item.value ? 'radio-on' : 'radio'} />
									</div>
								)}
							</div>
						</div>
					</div>
				</div>

				<div className="sports-priority__footer">
					<Button
						shape="capsule"
						onClick={saveBettingPreference}
					>
						{t('lbl_Done')}
					</Button>
				</div>
			</ScrollableModal>
		</>
	)
}